import React, { SyntheticEvent, useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardActionArea,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material'
import SvgData from 'assets/icons/circles/Data/Data'
import SvgMessage from 'assets/icons/circles/Message/Message'
import SvgPhoneCall from 'assets/icons/circles/PhoneCall/PhoneCall'
import HorizontalStackGrid from 'common/Grid/HorizontalStackGrid'
import VerticalStackGrid from 'common/Grid/VerticalStackGrid'
import numeral from 'numeral'
import { IBundlePlans } from 'types/api/v1/bundle.type'

import TabPanel from '../../../../common/TabPanel/TabPanel'
import { calculateOriginalBeforeDiscount } from '../../../../helpers/getDiscount'

interface BundlePlanProps {
  bundleData: IBundlePlans[]
  setSelection: (value: number) => void
  selectedIndexPlan: number
  setValue: (value: number) => void
  value: number
  setSelectedBundlePlan: (value: IBundlePlans) => void
  discountAmount: number
}

const BundlePlanOptions = ({
  bundleData,
  setValue,
  setSelectedBundlePlan,
  setSelection,
  selectedIndexPlan,
  discountAmount,
  value
}: BundlePlanProps) => {
  const bundleCategories = ['Circles One', 'Circles Data Only', 'Circles Three']

  const [selectedBundle, setSelectedBundle] = useState<IBundlePlans[]>([])

  const handleChange = (e: SyntheticEvent, newValue: number): void => {
    setValue(newValue)
    const bundleSelected = bundleData.filter(
      (data) => data.category === bundleCategories[newValue]
    )
    setSelectedBundle(bundleSelected)
    setSelectedBundlePlan(bundleSelected[0])
  }

  useEffect(() => {
    const firstBundle = bundleData.filter(
      (data) => data.category === bundleCategories[0]
    )
    setSelectedBundle(firstBundle)
  }, [bundleData])

  const handleCardSelection = (
    selectedIndex: number,
    planIdSelected: string
  ): void => {
    setSelection(selectedIndex)
    const bundleSelected = bundleData.filter(
      (data) => data.bundleId === planIdSelected
    )
    setSelectedBundlePlan(bundleSelected[0])
  }

  return (
    <>
      <Stack direction='row' alignItems='center'>
        <Typography
          variant='subtitle1'
          sx={{
            color: 'primary.dark',
            fontWeight: 500,
            paddingLeft: '16px'
          }}
        >
          Choose a plan:
        </Typography>
        <Tabs
          sx={{ marginBottom: '8px' }}
          value={value}
          onChange={handleChange}
          TabIndicatorProps={{
            sx: {
              backgroundColor: 'note.main'
            }
          }}
        >
          {bundleCategories.map((plan, index) => (
            <Tab
              data-cy={`viewBundleTab-${index}`}
              label={plan}
              key={`plan-${index}`}
              sx={{ marginLeft: '8px' }}
            />
          ))}
        </Tabs>
      </Stack>

      <Box
        sx={{
          marginBottom: '32px'
        }}
      >
        {bundleCategories.map((_, index) => (
          <TabPanel value={index} key={`plan-details-${index}`} index={value}>
            <Box data-cy={`bundlesView-${index}`}>
              {selectedBundle
                .sort((a, b) => Number(a.price.value) - Number(b.price.value))
                .map((plan, index) => (
                  <Card
                    variant='listCard'
                    key={`${plan.bundleId}`}
                    sx={{
                      ...(index === selectedIndexPlan && {
                        backgroundColor: '#E7F5FF',
                        border: '2px solid #3DA9FC'
                      })
                    }}
                  >
                    <CardActionArea
                      sx={{
                        padding: '20px'
                      }}
                      onClick={() => handleCardSelection(index, plan.bundleId)}
                    >
                      <Grid container spacing={2}>
                        <HorizontalStackGrid
                          xs={3}
                          sx={{
                            fontSize: '16px',
                            textTransform: 'capitalize',
                            color: 'primary.dark'
                          }}
                        >
                          {plan.description}
                        </HorizontalStackGrid>
                        <HorizontalStackGrid
                          xs={3}
                          sx={{
                            fontSize: '16px',
                            textTransform: 'capitalize',
                            fontWeight: 600,
                            color: 'primary.dark'
                          }}
                        >
                          {plan.price.symbol}{' '}
                          {calculateOriginalBeforeDiscount(
                            Number(plan.price.value),
                            plan,
                            discountAmount
                          )}
                          /mth
                        </HorizontalStackGrid>
                        <VerticalStackGrid
                          xs={2}
                          sx={{
                            fontSize: '12px',
                            textTransform: 'capitalize',
                            fontWeight: 400,
                            color: 'primary.dark'
                          }}
                        >
                          <Box>
                            <SvgData
                              style={{
                                transform: 'scale(0.8)'
                              }}
                            />
                          </Box>
                          {/* to change data */}
                          <Box>
                            {numeral(Number(plan.data) * 1000).format('0b')}
                          </Box>
                        </VerticalStackGrid>
                        <VerticalStackGrid
                          xs={2}
                          sx={{
                            fontSize: '12px',
                            textTransform: 'capitalize',
                            fontWeight: 400,
                            color: 'primary.dark'
                          }}
                        >
                          <Box>
                            <SvgPhoneCall
                              style={{
                                transform: 'scale(0.8)'
                              }}
                            />
                          </Box>
                          <Box>
                            {plan.voicePool === 'unlimited'
                              ? 'unlimited'
                              : `${plan.voicePool} mins`}
                          </Box>
                        </VerticalStackGrid>
                        <VerticalStackGrid
                          xs={2}
                          sx={{
                            fontSize: '12px',
                            textTransform: 'capitalize',
                            fontWeight: 400,
                            color: 'primary.dark'
                          }}
                        >
                          <Box>
                            <SvgMessage
                              style={{
                                transform: 'scale(0.8)'
                              }}
                            />
                          </Box>
                          {plan.sms} SMS
                        </VerticalStackGrid>
                      </Grid>
                    </CardActionArea>
                  </Card>
                ))}
            </Box>
          </TabPanel>
        ))}
      </Box>
    </>
  )
}

export default React.memo(BundlePlanOptions)
